import { Injectable, Injector } from '@angular/core';
import { AngularFirestoreCollection, AngularFirestoreDocument } from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FeedService } from 'src/pages/feed/services/feed/feed.service';
import { CalendarService } from 'src/shared/common/calendar/services/calendar/calendar.service';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Friend } from 'src/shared/models';

@Injectable({
  providedIn: 'root'
})
export class PeopleService extends Extender {
  public usersStoreRef: AngularFirestoreCollection<Account>;
  public friendsStoreRef: AngularFirestoreCollection<Friend>;
  public myFriendsStoreRef: AngularFirestoreCollection<Friend>;
  public user: Account;

  constructor(
    protected injector: Injector,
    // private calendarService: CalendarService,
    // private feedService: FeedService
  ) {
    super(injector);
    this.auth$.user$.subscribe((user) => {
      this.user = user;
    });
    this.usersStoreRef = this.afStore$.collection('users');
    this.friendsStoreRef = this.afStore$.collection('friends');
  }

  public getUsers(): Observable<any> {
    let users = [];
    return this.usersStoreRef.valueChanges().pipe(
      switchMap((_users: Account[]) => {
        users = _users;
        return this.getFriendsIds(this.user.uid);
      }),
      map((friends: string[]) => {
        return {
          friends: users.filter((person: Account) =>
            friends.includes(person.uid)
          ),
          users: users.filter(
            (person: Account) =>
              person.uid !== this.user.uid && !friends.includes(person.uid)
          )
        };
      })
    );
  }

  /**
   * @description sort and group people by first letter of thier name
   * @method groupPeople
   * @param people Account[]
   * @public
   * @return {void}
   */
  public groupPeople(
    people: Account[]
  ): Array<{ letter: string; people: Account[] }> {
    const sortedContacts = people.sort((a, b) => {
      return a.fullname.toLowerCase() > b.fullname.toLowerCase()
        ? 1
        : b.fullname.toLowerCase() > a.fullname.toLowerCase()
          ? -1
          : 0;
    });
    let currentLetter: boolean | any = false;
    let currentContacts = [];
    const groupedPeople = [];

    sortedContacts.forEach((value: Account) => {
      if (value.fullname.charAt(0).toString() !== currentLetter) {
        currentLetter = value.fullname.charAt(0);
        const newGroup = {
          letter: currentLetter,
          people: []
        };
        currentContacts = newGroup.people;
        groupedPeople.push(newGroup);
      }
      currentContacts.push(value);
    });

    return groupedPeople;
  }

  public getFriendsIds(id): Observable<any> {
    this.myFriendsStoreRef = this.afStore$.collection('friends', (ref) =>
      ref.where('uid', '==', id)
    );
    return this.myFriendsStoreRef.valueChanges().pipe(
      map((actions) => {
        if (actions.length > 0) {
          return actions.map((a) => {
            return a.friendId;
          });
        } else {
          return [];
        }
      })
    );
  }

  public getUserFriends(id) {
    const reads$ = [];
    return this.getFriendsIds(id).pipe(
      switchMap((friendIds: string[]) => {
        if (friendIds.length > 0) {
          friendIds.map((_id) => {
            reads$.push(this.getUser(_id));
          });
          return combineLatest(reads$);
        } else {
          return of([]);
        }
      }),
      map((data: Account[]) => {
        return data;
      })
    );
  }

  public getUser(id: string): Observable<any> {
    return this.usersStoreRef.doc(id).valueChanges();
  }

  // public getUserData(uid) {
  //   const reads$ = [];
  //   let contact;
  //   return this.getUser(uid).pipe(
  //     switchMap((_contact) => {
  //       contact = _contact;
  //       reads$.push(this.getUserFriends(contact.uid));
  //       reads$.push(this.getEvents(contact.uid));
  //       reads$.push(this.getFeed(contact.uid));
  //       return combineLatest(reads$);
  //     }),
  //     map((joins) => {
  //       return { contact, friends: joins[0], events: joins[1], feed: joins[2] };
  //     })
  //   );
  // }

  // public getEvents(id) {
  //   return this.calendarService.getUserEvents(id);
  // }
  // public getFeed(id) {
  //   return this.feedService.getFriendFeedData(100, id);
  // }

  public isFollowing(id) {
    return this.afStore$
      .collection('friends', (ref) =>
        ref.where('uid', '==', this.user.uid).where('friendId', '==', id)
      )
      .get()
      .pipe(
        map((docs) => {
          return docs.empty ? false : true;
        })
      );
  }

  /**
   * @description create friends model and saves new friend. appends the ui to show the chnages by adding the new friend to current contact
   * @method follow
   * @param contact Account
   * @public
   * @return {void}
   */
  public follow(contact: Account) {
    return this.friendsStoreRef
      .add({
        uid: this.user.uid,
        createdAt: Date.now(),
        friendId: contact.uid
      })
      .then(() => {
        this.toast(this.translate.instant('PeopleModule.new-friend'));
        return;
      })
      .catch((error) => {
        this.httpErrorMsg.handleError(error);
      });
  }

  /**
   * @description removes friends entry from database by first finding the friend object by current contact and friend ids.
   * Appends the ui to show the chnages by remoing the new friend from current contact's friends
   * @method unfollow
   * @param contact Account
   * @return {void}
   */
  public unfollow(contact: Account) {
    const subscription = this.afStore$
      .collection('friends', (ref) => ref.where('friendId', '==', contact.uid))
      .snapshotChanges()
      .subscribe((actions) => {
        actions.map((a) => {
          this.friendsStoreRef
              .doc(a.payload.doc.id)
              .delete()
              .then(() => {
                this.toast(
                    contact.fullname +
                    this.translate.instant('PeopleModule.no-friend')
                );
                subscription.unsubscribe()
                return;
              })
              .catch((error) => {
                this.httpErrorMsg.handleError(error);
              });
        });
      });
  }

  public updateUser(user: Account) {
    const userRef: AngularFirestoreDocument<Account> = this.afStore$.doc<any>(
      `users/${user.uid}`
    );
    return userRef.set(user, { merge: true });
  }
}
